import { ModalRef, useTranslation } from '@msdk/common'
import { ViewHeaderLinkDefinition } from '@msdk/definitions'
import { ButtonSecondary, Drawer, Link, styled, useComponentLibrary } from '@msdk/ui'
import React, { useEffect, useRef, useState } from 'react'

import { DrawerView } from '../../DrawerView'

const Container = styled('div', { shouldForwardProp: (prop) => prop !== '$width' })<{ $width?: string }>`
  height: 100%;
  width: ${(props) => (props.$width ? props.$width : '100%')};
  max-width: 50vw;
`

interface Props {
  link: ViewHeaderLinkDefinition
  reload: () => void
}

export function ViewLink(props: Props) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState<string | undefined>(undefined)

  const onLinkClick = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
    props.reload()
  }

  /**
   * Force setting a fixed width for the drawer once it has fitted the enclosing content.
   * We need to wait for the computation of '100%' width to be done before we can set the constant width.
   *
   * This is needed to allow the resizing of the columns without resizing the drawer.
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      /**
       * Exit early if the containerRef is not ready or the drawer is not open.
       */
      if (!containerRef?.current || !open) return

      /**
       * Get the calculated width of the container in px.
       */
      const currentContainerWidth = containerRef.current.getBoundingClientRect().width

      /**
       * Set the width of the container to the calculated width to make it constant.
       */
      setContainerWidth(`${currentContainerWidth}px`)
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [containerRef, open])

  const lib = useComponentLibrary()
  const LinkComponent = lib === 'nova' ? ButtonSecondary : Link

  return (
    <>
      <LinkComponent underline="hover" onClick={onLinkClick}>
        {t(props.link.idml, props.link.description)}
      </LinkComponent>
      <Drawer
        open={open}
        anchor="right"
        onClose={onClose}
        ModalProps={{
          BackdropProps: {
            invisible: true,
            onClickCapture: (event) => {
              event.stopPropagation()
            },
          },
        }}
      >
        <Container $width={containerWidth} ref={containerRef}>
          <DrawerView
            entityName={props.link.entityName}
            viewName={props.link.viewName}
            onClose={onClose}
            // Since the MUI Drawer does not behave like a modal (but for our purposes we are considering them the same),
            // we are manually creating a ModalRef to let the view know that we are inside a modal.
            // This way, it can behave correctly and open any other form inside a modal.
            modalRef={new ModalRef('', onClose)}
          />
        </Container>
      </Drawer>
    </>
  )
}
